// Ячейки-инструменты таблицы админки: текст правится на месте (клик → поле, Enter/уход сохраняет,
// Escape отменяет — как переименование строк на Launch), цвет — пипеткой с записью по закрытию,
// удаление — с подтверждением на месте, как «Not deploying?» на карточке релиза.
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@core/ui/cn';
import { useDismiss } from '@core/ui/dismiss';

export const CELL_INPUT = 'min-h-0 w-full border border-line rounded px-1.5 py-0.5 text-sm bg-white';

export function InlineText({ value, placeholder, editing: startEditing, hint, onSave, onCancel, className, data }: {
  value: string; placeholder?: string; editing?: boolean; className?: string; data?: Record<string, string>;
  /** Подсказка под полем по текущему вводу (похожие имена). */
  hint?: (text: string) => ReactNode;
  /** Вернуть false — остаться в правке (запись не прошла). Пустой текст — onCancel. */
  onSave: (text: string) => Promise<boolean> | boolean;
  onCancel?: () => void;
}) {
  const [editing, setEditing] = useState(!!startEditing);
  const [text, setText] = useState(value);
  const done = useRef(false); // Enter и уход с поля приходят оба: сохраняем один раз
  useEffect(() => { if (!editing) setText(value); }, [value, editing]);

  const commit = async () => {
    if (done.current) return;
    done.current = true;
    const t = text.trim();
    if (!t || t === value) { setEditing(false); if (!t) onCancel?.(); done.current = false; return; }
    const ok = await onSave(t);
    if (ok) setEditing(false);
    done.current = false;
  };
  const cancel = () => { done.current = true; setEditing(false); setText(value); onCancel?.(); done.current = false; };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void commit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };
  if (!editing) {
    return (
      <button type="button" {...data} className={cn('min-h-0 text-left rounded px-1 -mx-1 hover:bg-gray-100 truncate max-w-full', !value && 'text-ink-muted', className)}
        title="Click to edit" onClick={() => setEditing(true)}>{value || placeholder || '—'}</button>
    );
  }
  return (
    <span className="relative block">
      <input {...data} autoFocus className={CELL_INPUT} value={text} placeholder={placeholder} aria-label={placeholder ?? 'Value'}
        onChange={(e) => setText(e.target.value)} onBlur={() => void commit()} onKeyDown={onKey} />
      {hint && <span className="absolute left-0 top-full z-10 mt-0.5">{hint(text)}</span>}
    </span>
  );
}

/** Пипетка: запись по нативному change (пикер закрыт), а не на каждое движение — иначе десятки записей подряд. */
export function ColorCell({ value, label, onSave }: { value: string; label: string; onSave: (color: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState<string | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = () => { onSave(el.value); setPicking(null); };
    el.addEventListener('change', h);
    return () => el.removeEventListener('change', h);
  }, [onSave]);
  const color = picking ?? value;
  return (
    <label className="relative inline-flex shrink-0 cursor-pointer align-middle" title={`Colour of ${label}`}>
      <span className="inline-block w-3.5 h-3.5 rounded-full ring-1 ring-black/10" style={{ background: color }} aria-hidden />
      <input ref={ref} type="color" value={color} aria-label={`Colour of ${label}`} className="absolute inset-0 w-3.5 h-3.5 opacity-0 cursor-pointer" onChange={(e) => setPicking(e.target.value)} />
    </label>
  );
}

/**
 * × с подтверждением на месте: строка не уезжает в модалку ради одного вопроса. Вопрос всплывает
 * поверх таблицы, а не в ячейке — иначе узкая колонка крестика растягивалась и ломала колонки.
 */
export function ConfirmX({ question, label, busy, onConfirm, data, extra }: { question: ReactNode; label: string; busy?: boolean; onConfirm: () => void; data?: Record<string, string>; extra?: (close: () => void) => ReactNode }) {
  const [asking, setAsking] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const close = useCallback(() => setAsking(false), []);
  useDismiss(ref, asking, close);
  return (
    <span ref={ref} className="relative inline-block">
      <button type="button" {...data} className={cn('min-h-0 w-6 h-6 rounded text-ink-muted hover:text-danger hover:bg-gray-100 leading-none', asking && 'bg-gray-100 text-danger')} title={label} aria-label={label} aria-expanded={asking} onClick={() => setAsking((a) => !a)}>×</button>
      {asking && (
        <span role="dialog" aria-label={label} className="absolute left-0 top-full z-20 mt-1 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs shadow-md">
          <span className="font-medium">{question}</span>
          <button type="button" className="min-h-0 px-2 py-0.5 rounded bg-danger text-white font-medium disabled:opacity-50" disabled={busy} onClick={() => { setAsking(false); onConfirm(); }}>Yes</button>
          <button type="button" className="min-h-0 px-2 py-0.5 rounded hover:bg-gray-200" onClick={close}>No</button>
          {extra?.(close)}
        </span>
      )}
    </span>
  );
}

export const SELECT = 'min-h-0 border border-line rounded px-1.5 py-0.5 text-sm bg-white';
export const TH = 'text-left font-semibold text-xs uppercase tracking-wide text-ink-muted px-2 py-1.5 select-none whitespace-nowrap';
export const TD = 'px-2 py-1 align-middle';
